import React, { useEffect, useState } from 'react'
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, InputAdornment, TextField, Typography,
} from '@mui/material'
import VisibilityIcon    from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import { supabase } from '../../supabaseClient'
import { readFunctionError } from '../../lib/functionError'

export interface SetPasswordTarget {
  user_id:    string
  email:      string
  first_name: string
  last_name:  string
}

interface Props {
  open:    boolean
  target:  SetPasswordTarget | null
  onClose: () => void
  onSaved: () => void
}

export default function SetStudentPasswordDialog({ open, target, onClose, onSaved }: Props) {
  const [password, setPassword] = useState('')
  const [confirm,  setConfirm]  = useState('')
  const [show,     setShow]     = useState(false)
  const [saving,   setSaving]   = useState(false)
  const [error,    setError]    = useState<string | null>(null)
  const [success,  setSuccess]  = useState(false)

  useEffect(() => {
    if (!open) return
    setPassword(''); setConfirm(''); setShow(false); setError(null); setSuccess(false)
  }, [open])

  async function handleSave() {
    if (!target) return
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return }
    if (password.length > 72) { setError('Password must be 72 characters or fewer.'); return }
    if (password !== confirm) { setError('Passwords do not match.'); return }
    setSaving(true)
    setError(null)
    const { data, error: invokeErr } = await supabase.functions.invoke('set-student-password', {
      body: { user_id: target.user_id, password },
    })
    if (invokeErr) { setError(await readFunctionError(invokeErr)); setSaving(false); return }
    if (data?.error) { setError(data.error); setSaving(false); return }
    setSaving(false)
    setSuccess(true)
    onSaved()
  }

  const fullName = target ? `${target.first_name} ${target.last_name}`.trim() || target.email : ''

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Set Password</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {fullName} ({target?.email})
        </Typography>

        {success ? (
          <Alert severity="success">
            Password set. Tell <strong>{target?.first_name}</strong> their temporary password —
            they'll be asked to choose a new one on first sign-in.
          </Alert>
        ) : (
          <>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Temporary password"
                size="small" fullWidth required
                type={show ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setShow(s => !s)} tabIndex={-1}>
                          {show ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />
              <TextField
                label="Confirm password"
                size="small" fullWidth required
                type={show ? 'text' : 'password'}
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
              />
              <Typography variant="caption" color="text.secondary">
                Tell the student this password out of band (in person, phone, etc.). They'll be
                forced to change it on next sign-in.
              </Typography>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>{success ? 'Close' : 'Cancel'}</Button>
        {!success && (
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Set Password'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
